import { __ } from "i18n-for-browser";
import {
  append,
  compose,
  concat,
  endsWith,
  find,
  map,
  path,
  prop,
  sortBy,
  toLower,
  clone,
  isEmpty
} from "ramda";
import Lisatiedot from "../../lisatiedot";
import { createEsikatseluHTML } from "@/helpers/esikatselu";
import { sortEsikatseluRajoitteet } from "@/helpers/html";
export async function previewOfOpetuskielet(
  { lomakedata, rajoitteet, maaraykset },
  booleans,
  locale
) {
  let structure = [];

  const ensisijaiset = findByAnchorSuffix(".ensisijaiset", lomakedata);
  const toissijaiset = findByAnchorSuffix(".toissijaiset", lomakedata);
  const opiskelijamaara = findByAnchorSuffix(".lkm", lomakedata);
  const kuvaus = findByAnchorSuffix(".kuvaus", lomakedata);

  const isEnglishSelected =
    Array.isArray(ensisijaiset.properties?.value) &&
    ensisijaiset.properties.value.some(item => item.value === "EN");

  const updatedEnsisijaiset = updateEnsisijaisetEnglishLabel(
    ensisijaiset,
    isEnglishSelected,
    opiskelijamaara,
    kuvaus
  );

  const ensisijaisetListItems = getKieletPreview(
    updatedEnsisijaiset,
    rajoitteet,
    maaraykset,
    locale
  );

  const toissijaisetListItems = getKieletPreview(
    toissijaiset,
    rajoitteet,
    maaraykset,
    locale
  );

  if (ensisijaisetListItems.length) {
    structure = append(
      {
        anchor: "ensisijaiset",
        components: [
          {
            anchor: "listaus",
            name: "List",
            properties: {
              isDense: true,
              items: ensisijaisetListItems
            }
          }
        ]
      },
      structure
    );
  }

  if (toissijaisetListItems.length) {
    structure = concat(structure, [
      {
        anchor: "muilla-kielilla",
        components: [
          {
            anchor: "otsikko",
            name: "FormTitle",
            properties: {
              isPreviewModeOn: true,
              level: 4,
              title: __("education.voidaanAntaaMyosSeuraavillaKielilla")
            }
          }
        ]
      },
      {
        anchor: "toissijaiset",
        components: [
          {
            anchor: "listaus",
            name: "List",
            properties: {
              isDense: true,
              items: toissijaisetListItems
            }
          }
        ]
      }
    ]);
  }

  const lisatiedotNode = findByAnchorSuffix(".lisatiedot.1", lomakedata);

  if (lisatiedotNode && lisatiedotNode.properties.value) {
    structure = append(Lisatiedot(lisatiedotNode.properties.value), structure);
  }

  return structure;
}

const findByAnchorSuffix = (suffix, data) =>
  find(compose(endsWith(suffix), prop("anchor")), data);

/**
 * Funktiossa päivitetään ensisijaiset objektin label-kenttä, jos englannin opiskelijamäärää ei ole määritetty.
 * Arvoa käytetään myöhemmin esikatselutekstin muodostamisessa ja se muutetaan muotoon "englanti enintään X opiskelijaa".
 * Päivitys tehdään vain silloin, jos englanti on valittu valikosta.
 *
 * @param ensisijaiset sisältää valikossa valitut ensisijaiset opetuskielet
 * @param isEnglishSelected true tai false sen perusteella onko englanti valittu
 * @param opiskelijamaara englantiin liittyvä opiskelijamäärä
 * @param kuvaus englantiin liittyvä kuvaus
 * @returns päivitetty label-kenttä, jota myöhemmin käytetään esikatselun muodostamisessa (esim. englanti enintään X opiskelijaa)
 */
function updateEnsisijaisetEnglishLabel(
  ensisijaiset,
  isEnglishSelected,
  opiskelijamaara,
  kuvaus
) {
  if (!isEnglishSelected) return ensisijaiset;
  if (Number(opiskelijamaara?.properties?.value) === 0 || Number.isNaN(opiskelijamaara?.properties?.value)) return ensisijaiset;

  const label = {
    label: `${kuvaus?.properties?.value || ""} enintään ${
      opiskelijamaara?.properties?.value || 0
    } opiskelijaa`
  };

  const updatedLabel = (ensisijaiset.properties.value || []).map(item =>
    item.value === "EN" ? { ...item, label: label.label } : item
  );

  return {
    ...ensisijaiset,
    properties: {
      ...ensisijaiset.properties,
      value: updatedLabel
    }
  };
}

const getKieletPreview = (kielet, rajoitteet = [], maaraykset, locale) => {
  // Järjesteää kielet aakkos järjestykseen
  const kohdistuvatKielet = clone(kielet);
  if (kohdistuvatKielet) {
    kohdistuvatKielet.properties.value = sortBy(
      compose(toLower, path(["label"]))
    )(path(["properties", "value"])(kohdistuvatKielet) || []);
  }

  const kohdistuvatRajoitemuutosobjektit = sortEsikatseluRajoitteet(rajoitteet);

  return kohdistuvatKielet
    ? sortBy(
      prop("content"),
      map(opetuskieli => {
        const koodiarvo = opetuskieli.value;
        const maarays = find(
          maarays =>
            toLower(maarays.koodiarvo) === toLower(koodiarvo) &&
            maarays.koodisto === "kielikoodistoopetushallinto",
          maaraykset
        );

        const html = createEsikatseluHTML(
          maarays,
          koodiarvo,
          kohdistuvatRajoitemuutosobjektit,
          locale,
          "nimi",
          opetuskieli.label
        );
        if (isEmpty(html)) {
          return null;
        }
        return {
          anchor: "opetuskieli",
          components: [
            {
              anchor: opetuskieli.value,
              name: "HtmlContent",
              properties: {
                content: html
              }
            }
          ]
        };
      }, path(["properties", "value"], kohdistuvatKielet).filter(Boolean) || []).filter(
        Boolean
      )
    )
    : [];
};
